////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// turn-widget-env-forms.ts — client-side "Change Encounter Light Type" / "Change Encounter Weather" forms.
// ----------------------------------------------------------------------------------------------------
// Replaces the old PHP+AJAX round-trips that lived in includes/js/template.js
// (changeEncounterLightType / changeEncounterLightTypeTo / changeEncounterWeather /
// changeEncounterWeatherTo). The "type" catalogs are STATIC (LightType::LIST / WeatherType::LIST in
// additions.php, mirrored in ../shared/env-types), so the button grid is rendered entirely on the
// client into #div_refresh_gm_viewer — no form-fetch request.
//
// A saved choice is published by the browser through its reconnect outbox. The field-level adopter
// emits `ft:encounter-lighting-changed` for the renderer, open tile grid and sidelog summary.
//
// Bundled into includes/js/dist/turn-widget-bundle.js (loaded AFTER template.js), and the four
// functions are window-attached via ./index.ts so the inline onclick="changeEncounterLightType()" in
// turn-widget-gm-controls.ts Row 1 and each grid tile keep resolving.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { EncounterArr } from '../shared/array-types';
import { encounterField } from '../shared/array-types';
import { LIGHT_TYPE_LIST, WEATHER_TYPE_LIST, type EnvTypeRow } from '../shared/env-types';
import type { AppliedFrame } from '../janus-engine/transport/applied-frame';
import type { TruthPush } from '../shared/applied-truth-transport';
import { withAsyncDeadline } from '../shared/async-deadline';

const GM_VIEWER_DIV = 'div_refresh_gm_viewer';
const WARNING_DIV    = 'div_sidelog_warning';

// One config per environmental dimension keeps the two flows a single code path.
interface EnvFormKind {
    key: 'light' | 'weather';
    wrapperId: string;          // grid wrapper id (scopes the optimistic active-tile flip)
    titleIconKey: string;       // iconMap() key for the form heading
    encounterField: string;     // encounter_arr field holding the current selection
    list: ReadonlyArray<EnvTypeRow>;
    endpoint: string;           // PHP set-handler (relative to BASE_URL)
    postKey: string;            // POST body key the handler reads
    setFnName: string;          // global tile-onclick fn name
    title: string;
    help: string;
    descriptionOf: (row: EnvTypeRow) => string;
    allowAutomatic?: boolean;
}

const LIGHT_KIND: EnvFormKind = {
    key: 'light',
    wrapperId: 'div_encounter_light_type_form',
    titleIconKey: 'light_type',
    encounterField: 'encounter_light',
    list: LIGHT_TYPE_LIST,
    endpoint: 'options/tools/encounters/encounter_light/change_encounter_light_type_to_inc.php',
    postKey: 'change_encounter_light_type_to',
    setFnName: 'changeEncounterLightTypeTo',
    title: 'Encounter Lighting',
    help: 'Automatic follows the campaign clock. Any other choice overrides the complete visual mood for this encounter.',
    descriptionOf: row => ({
        automatic: 'Follow campaign time and sun.',
        bright_light: 'Force a clear daytime presentation.',
        dim_light: 'Force a dim sunset presentation.',
        pitch_black: 'Force a full night presentation.',
        magical_darkness: 'Force supernatural magical night.',
    } as Record<string, string>)[row.variable_name] || row.item_name,
    allowAutomatic: true,
};

const WEATHER_KIND: EnvFormKind = {
    key: 'weather',
    wrapperId: 'div_encounter_weather_form',
    titleIconKey: 'weather',
    encounterField: 'encounter_weather',
    list: WEATHER_TYPE_LIST,
    endpoint: 'options/tools/encounters/encounter_weather/change_encounter_weather_to_inc.php',
    postKey: 'change_encounter_weather_to',
    setFnName: 'changeEncounterWeatherTo',
    title: 'Encounter Weather',
    help: 'Choose the weather applied across this encounter.',
    descriptionOf: row => `Apply ${row.item_name.toLowerCase()} to the encounter.`,
};

function _ftEnvEsc(s: unknown): string {
    if (typeof escapeHtml === 'function') return escapeHtml(s == null ? '' : String(s));
    const esc: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => esc[c] || c);
}

// Pull the live encounter_arr for the open encounter out of the global cache so the form can
// highlight the current selection and build its heading. Returns null on a cold cache (the grid
// still renders — just with nothing pre-highlighted).
function _ftEnvCachedEncounter(encounterId: string): EncounterArr | null {
    const cache = (window as { FateTide?: { encounterCache?: Map<string, unknown> } }).FateTide?.encounterCache;
    if (cache instanceof Map && cache.has(encounterId)) {
        return (cache.get(encounterId) as EncounterArr) || null;
    }
    return null;
}

const TILE_ACTIVE_CLASS   = 'btn btn-primary border m-1 ft-env-tile';
const TILE_INACTIVE_CLASS = 'btn btn-outline-light m-1 ft-env-tile';

// The workspace chrome already establishes which encounter is open. Keep the form heading focused
// on the action so encounter/location names do not compete with the environmental control title.
function _ftEnvHeading(kind: EnvFormKind): string {
    const icon = typeof iconMap === 'function' ? iconMap(kind.titleIconKey) : '';
    return `
        <div class="ft-env-form__heading">
            <h2 class="ft-env-form__title">${icon}<span>${_ftEnvEsc(kind.title)}</span></h2>
            <p>${_ftEnvEsc(kind.help)}</p>
        </div>`;
}

function _ftEnvGmVisibilityControl(): string {
    const api = window as Window & { ftToggleFt3dGmVisibility?: () => void };
    if (typeof api.ftToggleFt3dGmVisibility !== 'function') return '';
    const gmVisionIcon = typeof iconMap === 'function' ? iconMap('gm_vision', 20, 20, false) : '';
    return `
        <section class="ft-gm-visibility-control" aria-labelledby="ft_gm_visibility_title">
            <div>
                <strong id="ft_gm_visibility_title">GM Vision</strong>
                <span>Temporarily reveal dark scenes for you only. Players and encounter rules are unchanged.</span>
            </div>
            <button
                type="button"
                class="btn btn-sm btn-outline-info"
                data-ft-turn-widget-action="toggle-gm-visibility"
                data-ft3d-gm-visibility-toggle
                aria-pressed="false"
                title="Toggle GM Vision (Shift+L)"
            >
                ${gmVisionIcon}
                <span data-ft3d-gm-visibility-state>Actual lighting</span>
                <kbd>Shift+L</kbd>
            </button>
        </section>`;
}

// Render the full client-side grid for a kind and inject it into the GM viewer pane.
function _ftEnvRenderForm(kind: EnvFormKind): void {
    const ctx = typeof getSidelogContext === 'function' ? getSidelogContext() : null;
    if (!ctx) return;
    const encounterId = ctx.encounter_id || '';
    if (!encounterId) {
        console.error(`${kind.setFnName}: missing encounter_id in sidelog context`);
        return;
    }

    const enc = _ftEnvCachedEncounter(encounterId);
    const current = enc ? String(encounterField(enc, kind.encounterField) ?? '') : '';
    const currentUpper = current.toUpperCase();

    const rows: ReadonlyArray<EnvTypeRow> = kind.allowAutomatic
        ? [{ item_id: '', item_name: 'Automatic', variable_name: 'automatic' }, ...kind.list]
        : kind.list;
    const tiles = rows.map(row => {
        const isActive = row.item_id.toUpperCase() === currentUpper;
        const cls = isActive ? TILE_ACTIVE_CLASS : TILE_INACTIVE_CLASS;
        const iconKey = row.item_id || kind.titleIconKey;
        const icon = typeof iconMap === 'function' ? iconMap(iconKey) : '';
        return `
            <button
                type="button"
                class="${cls}"
                data-env-id="${_ftEnvEsc(row.item_id)}"
                onclick="${kind.setFnName}('${_ftEnvEsc(row.item_id)}');"
                aria-pressed="${isActive ? 'true' : 'false'}"
            >
                <span class="ft-env-tile__icon">${icon}</span>
                <span class="ft-env-tile__copy">
                    <strong>${_ftEnvEsc(row.item_name)}</strong>
                    <small>${_ftEnvEsc(kind.descriptionOf(row))}</small>
                </span>
                <span class="ft-env-tile__active" ${isActive ? '' : 'hidden'}>Current</span>
            </button>`;
    }).join('');

    const html = `
<div class="ft-env-form" id="${kind.wrapperId}">
    <div class="ft-env-form__body">
        ${_ftEnvHeading(kind)}
        <div class="ft-env-form__grid">${tiles}
        </div>
        ${kind.key === 'light' ? _ftEnvGmVisibilityControl() : ''}
    </div>
</div>`;

    const host = document.getElementById(GM_VIEWER_DIV);
    if (host) host.innerHTML = html;
    const gmVisibilityApi = window as Window & { ftSyncFt3dGmVisibilityControls?: () => void };
    gmVisibilityApi.ftSyncFt3dGmVisibilityControls?.();
    // iconMap() returns final inline icon HTML, so no ftMountIcons pass is required here.
}

// Update the active tile only after the authoritative compact frame has been applied.
function _ftEnvFlipActive(kind: EnvFormKind, uuid: string): void {
    const wrapper = document.getElementById(kind.wrapperId);
    if (!wrapper) return;
    const want = String(uuid).toUpperCase();
    wrapper.querySelectorAll<HTMLElement>('.ft-env-tile').forEach(tile => {
        const id = (tile.getAttribute('data-env-id') || '').toUpperCase();
        const active = id === want;
        tile.className = active ? TILE_ACTIVE_CLASS : TILE_INACTIVE_CLASS;
        tile.setAttribute('aria-pressed', active ? 'true' : 'false');
        const badge = tile.querySelector<HTMLElement>('.ft-env-tile__active');
        if (badge) badge.hidden = !active;
    });
}

interface EnvLightingChangedDetail {
    encounterId?: string;
    encounterArr?: EncounterArr | null;
    encounterLightId?: string | null;
    encounterWeatherId?: string | null;
}

// Project applied encounter truth into the remaining form/summary views.
function _ftEnvProjectConfirmedChange(event: Event): void {
    const detail = (event as CustomEvent<EnvLightingChangedDetail>).detail || {};
    const ctx = typeof getSidelogContext === 'function' ? getSidelogContext() : null;
    const encounterId = String(ctx?.encounter_id || '');
    if (!encounterId || String(detail.encounterId || '').toUpperCase() !== encounterId.toUpperCase()) return;

    const hasLight = Object.prototype.hasOwnProperty.call(detail, 'encounterLightId');
    const hasWeather = Object.prototype.hasOwnProperty.call(detail, 'encounterWeatherId');
    if (hasLight) _ftEnvFlipActive(LIGHT_KIND, String(detail.encounterLightId || ''));
    if (hasWeather) _ftEnvFlipActive(WEATHER_KIND, String(detail.encounterWeatherId || ''));

    const encounterArr = detail.encounterArr || _ftEnvCachedEncounter(encounterId);
    if (event.type === 'ft:encounter-state-received' && encounterArr) {
        _ftEnvFlipActive(LIGHT_KIND, String(encounterField(encounterArr, LIGHT_KIND.encounterField) || ''));
        _ftEnvFlipActive(WEATHER_KIND, String(encounterField(encounterArr, WEATHER_KIND.encounterField) || ''));
    }
    if ((hasLight || hasWeather) && encounterArr && typeof getEncounterSettingSummary === 'function') {
        try { void getEncounterSettingSummary(encounterArr, null); } catch (e) { /* non-fatal */ }
    }
}

window.addEventListener('ft:encounter-lighting-changed', _ftEnvProjectConfirmedChange);
window.addEventListener('ft:encounter-state-received', _ftEnvProjectConfirmedChange);

// Both environmental pickers use the same saved-row publication lane.
async function _ftEnvApply(kind: EnvFormKind, uuid: string): Promise<void> {
    if (typeof clearLegacySelection === 'function') clearLegacySelection();

    const ctx = typeof getSidelogContext === 'function' ? getSidelogContext() : null;
    if (!ctx) return;
    const campaign_id  = ctx.campaign_id || '';
    const encounter_id = ctx.encounter_id || '';

    const validValue = (kind.allowAutomatic === true && uuid === '') || checkIfValidUUID(uuid);
    if (!checkIfValidUUID(campaign_id) || !checkIfValidUUID(encounter_id) || !validValue) {
        console.error(`${kind.setFnName}: invalid campaign_id / encounter_id / ${kind.postKey}`, { campaign_id, encounter_id, uuid });
        return;
    }

    let owner: Window = window;
    try {
        if (window.parent !== window && window.parent.ftPushAppliedTruth
            && ((window.parent as Window & { ws?: unknown }).ws || !window.ftPushAppliedTruth)) owner = window.parent;
    } catch { /* cross-origin embed */ }
    try {
        if (!owner.ftPushAppliedTruth) throw new Error('encounter-environment:truth-transport-unavailable');
        const { response, saved } = await withAsyncDeadline(async signal => {
            const response = await fetch(BASE_URL + kind.endpoint, {
                method: 'POST', credentials: 'same-origin', signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ campaign_id, encounter_id, [kind.postKey]: uuid }),
            });
            const saved = await response.clone().json() as { ok?: boolean; error?: string; row?: Record<string, unknown>; saved_rolls?: unknown[] };
            return { response, saved };
        }, 16000);
        if (!response.ok || !saved.ok || !saved.row) throw new Error(saved.error || 'encounter-environment:save-refused');
        const operationId = `encounter-environment:${crypto.randomUUID().toUpperCase()}`;
        const id = String(encounterField(saved.row, 'encounter_id') ?? '');
        const value = encounterField(saved.row, kind.encounterField) ?? null;
        const frame: AppliedFrame = { kind: 'applied', operation_id: operationId, encounter_id: id, actor_id: id,
            rev: 0, member_revs: {}, computed: {}, new_entities: [], deletes: [],
            rows: [{ table: 'encounters', row_id: id, partial: true, row: { encounter_id: id, [kind.encounterField]: value } }] };
        const push: TruthPush = { type: 'janus_truth_presentation', command: { operation_id: operationId,
            encounter_id: id, actor_id: id, saved_rolls: saved.saved_rolls }, applied_frame: frame };
        await withAsyncDeadline(() => Promise.resolve(owner.ftPushAppliedTruth!(push)), 16000);
        await owner.ftSaveAppliedTruth?.(operationId, async () => response);
        const warning = document.getElementById(WARNING_DIV);
        if (warning) warning.textContent = '';
    } catch (error) {
        displayWarning(error instanceof Error ? error.message : 'encounter-environment:save-failed');
        return;
    }

    if (kind.key === 'weather' && typeof playCampaignEncounterAmb === 'function') {
        try { await playCampaignEncounterAmb(); } catch (e) { /* non-fatal */ }
    }
}

// ── Public surface (window-attached via index.ts; called from inline onclick handlers) ──
export function changeEncounterLightType(): void { _ftEnvRenderForm(LIGHT_KIND); }
export function changeEncounterWeather(): void { _ftEnvRenderForm(WEATHER_KIND); }
export function changeEncounterLightTypeTo(uuid: string): Promise<void> { return _ftEnvApply(LIGHT_KIND, uuid); }
export function changeEncounterWeatherTo(uuid: string): Promise<void> { return _ftEnvApply(WEATHER_KIND, uuid); }
